import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import CheckoutEventHeader from '@/components/CheckoutEventHeader';

/**
 * ── /components/checkout-event-header ────────────────────────────────────
 *
 * CheckoutEventHeader — the event identity block atop the three post-booking
 * receipt screens: events/[id]/confirm.tsx (payment confirmation),
 * bookings/[id]/index.tsx, and requests/[id]/index.tsx. All three call it the
 * same way — imageUrl, title, slotDisplay (pre-formatted via
 * formatDateTime), venueName, googleMapsUrl — which is the component's
 * entire prop surface; covers the title's line clamp and the fixed image
 * box, plus the slot/venue row rendered via EventLocation.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Real call sites all pass the same five props (grepped events/[id]/confirm.tsx,
// bookings/[id]/index.tsx, requests/[id]/index.tsx — identical call shape).
// Long title exercises the line-clamp (PSText.H1 numberOfLines={5}).
const PREVIEW_HEADERS = [
  {
    id: 'ceh-long',
    label: 'long title — clamps at 5 lines (PSText.H1 numberOfLines={5})',
    imageUrl: 'https://picsum.photos/id/1015/600/800',
    title:
      'The Grand Midsummer Rooftop Vinyl Sessions: An Eight-Night Residency of Deep-Listening Sets, Natural Wine & Skyline Views Above Marine Drive',
    slotDisplay: 'Sat, 20 Sep · 7:00 PM',
    venueName: 'Marine Drive Rooftop',
    googleMapsUrl: 'https://maps.google.com/?q=Marine+Drive+Rooftop',
  },
  {
    id: 'ceh-short',
    label:
      'short title — one line, image box stays fixed 112×149.3 (4:3) either way',
    imageUrl: 'https://picsum.photos/id/1043/600/800',
    title: 'Pottery Workshop',
    slotDisplay: 'Sun, 21 Sep · 11:00 AM',
    venueName: 'Kala Studio',
    googleMapsUrl: 'https://maps.google.com/?q=Kala+Studio',
  },
] as const;

export default function CheckoutEventHeaderPreview() {
  return (
    <>
      <Head>
        <title>CheckoutEventHeader · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Both fixtures, stacked — same call shape as the three real
            screens (imageUrl + title only). Long title first so the 4-line
            clamp/ellipse is the first thing eyeballed; short title after to
            confirm nothing about layout depends on the title actually
            filling the column (the image is a fixed box regardless). */}
          <YStack alignSelf="stretch" gap="$lg">
            {PREVIEW_HEADERS.map((h) => (
              <YStack key={h.id} gap="$xs">
                <Text
                  fontFamily="$body"
                  fontSize="$small"
                  color="$textDarkMuted"
                >
                  {h.label}
                </Text>
                <CheckoutEventHeader
                  imageUrl={h.imageUrl}
                  title={h.title}
                  slotDisplay={h.slotDisplay}
                  venueName={h.venueName}
                  googleMapsUrl={h.googleMapsUrl}
                />
              </YStack>
            ))}
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
